"use client";

import React, { useState } from "react";
import { useAuth } from "@/src/contexts/AuthContext";
import { updateBuyerProfile, type BuyerProfileUpdate } from "@/src/services/buyerProfile.service";

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
] as const;

/**
 * First-time buyers sign in with just their phone number, so we collect the
 * rest of the profile once — right after login — instead of cluttering the OTP
 * form. Shows only while the backend reports `isFirstTimeUser` and the name is
 * still empty. Name is required; email and gender are optional.
 */
export function FirstTimeProfilePrompt() {
  const { needsName, token, setName } = useAuth();
  const [name, setNameValue] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!needsName) return null;

  const handleSave = async () => {
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) { setError("Please enter your name"); return; }
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Enter a valid email address");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await updateBuyerProfile(token!, {
        name: trimmedName,
        ...(trimmedEmail ? { email: trimmedEmail } : {}),
        ...(gender ? { gender: gender as BuyerProfileUpdate["gender"] } : {}),
      });
      setName(trimmedName);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't save your details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 px-4 backdrop-blur-sm">
      <div className="w-full max-w-[420px] overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="px-8 py-7 text-center" style={{ background: "#F26250" }}>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/15">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
            </svg>
          </div>
          <h2 className="mt-4 text-[20px] font-bold text-white">Tell us about you</h2>
          <p className="mt-1.5 text-sm text-white/75">A few details so we can personalise your orders</p>
        </div>

        <div className="space-y-4 px-8 py-7">
          <div>
            <label htmlFor="ft-name" className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A85]">
              Name *
            </label>
            <input
              id="ft-name"
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              placeholder="Your name"
              className="w-full rounded-xl border border-[#DED9CF] bg-[#FAFAF8] px-4 py-3.5 text-sm text-[#1D1D1D] placeholder-[#C0BDB7] outline-none transition focus:border-[#F26250] focus:bg-white"
            />
          </div>

          <div>
            <label htmlFor="ft-email" className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A85]">
              Email
            </label>
            <input
              id="ft-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              placeholder="you@example.com (optional)"
              className="w-full rounded-xl border border-[#DED9CF] bg-[#FAFAF8] px-4 py-3.5 text-sm text-[#1D1D1D] placeholder-[#C0BDB7] outline-none transition focus:border-[#F26250] focus:bg-white"
            />
          </div>

          <div>
            <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A85]">Gender</p>
            <div className="flex flex-wrap gap-2">
              {GENDER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setGender(gender === opt.value ? "" : opt.value)}
                  className="rounded-full px-4 py-2 text-xs font-bold transition"
                  style={gender === opt.value
                    ? { background: "#F26250", color: "#fff" }
                    : { border: "1px solid #DED9CF", color: "#8A8A85" }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-[#F26250]">{error}</p>}

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full rounded-xl bg-[#F26250] py-4 text-sm font-bold text-white transition hover:bg-[#D94A39] disabled:opacity-50"
          >
            {saving ? "Saving..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
